import $ from "jquery";

import render_sign_message_receipt_modal from "../templates/sign_message_receipt_modal.hbs";

import * as channel from "./channel.ts";
import * as dialog_widget from "./dialog_widget.ts";
import {$t, $t_html} from "./i18n.ts";
import {
    type MessageContainer,
    type MessageGroup,
    get_timestr,
    populate_group_from_message,
} from "./message_list_view.ts";
import type {Message} from "./message_store.ts";
import * as people from "./people.ts";
import {update_elements} from "./rendered_markdown.ts";
import * as rows from "./rows.ts";
import * as stream_data from "./stream_data.ts";
import {process_submessages} from "./submessage.ts";
import * as ui_report from "./ui_report.ts";
import {toggle_user_card_popover_for_message} from "./user_card_popover.ts";
import {current_user} from "./state_data.ts";

interface SigningAccountOption {
    user_id: number;
    is_current: boolean;
    is_group_account: boolean;
    is_individual_group: boolean;
    can_sign: boolean;
    requires_representative: boolean;
    is_individual_signer: boolean;
    display_name: string;
}

interface SigningAccountsResponse {
    accounts: SigningAccountOption[];
}

interface SignatureVisibilityResponse {
    can_toggle_signature_page_visibility: boolean;
    signature_page_is_public: boolean;
    show_signing_buttons: boolean;
    show_sign_edited_post_button: boolean;
}

function register_message_preview_click_handlers(
    $message_preview_container: JQuery,
    sender_id: number,
): void {
    $message_preview_container.on("mouseover", ".sender_info_hover", function (this: HTMLElement) {
        const $row = $(this).closest(".message_row");
        $row.addClass("sender_info_hovered");
    });

    $message_preview_container.on("mouseout", ".sender_info_hover", function (this: HTMLElement) {
        const $row = $(this).closest(".message_row");
        $row.removeClass("sender_info_hovered");
    });

    $message_preview_container.on(
        "click",
        ".sender_name, .inline-profile-picture-wrapper",
        function (this: HTMLElement, e) {
            e.stopPropagation();
            toggle_user_card_popover_for_message(this, sender_id, sender_id, true);
        },
    );
}

function get_message_group_for_message_preview(message: Message): MessageGroup {
    const message_group = populate_group_from_message(message, false, false, undefined);
    if (message_group.is_stream) {
        message_group.user_can_resolve_topic = false;
        message_group.is_topic_editable = false;
    }
    return message_group;
}

function get_message_container_for_preview(message: Message): MessageContainer {
    const computed_variables = {
        include_sender: true,
        is_hidden: false,
        msg: message,
        sender_is_bot: people.sender_is_bot(message),
        sender_is_deactivated: people.sender_is_deactivated(message),
        sender_is_guest: people.sender_is_guest(message),
        should_add_guest_indicator_for_sender: people.should_add_guest_user_indicator(
            message.sender_id,
        ),
        small_avatar_url: people.small_avatar_url(message),
        status_message: "",
        timestr: get_timestr(message),
        want_date_divider: false,
        want_subscription_status_divider: false,
    };
    const unused_variables = {
        date_divider_html: undefined,
        edited: false,
        include_recipient: false,
        last_edit_timestamp: undefined,
        last_moved_timestamp: undefined,
        mention_classname: undefined,
        message_edit_notices_alongside_sender: false,
        message_edit_notices_for_status_message: false,
        message_edit_notices_in_left_col: false,
        modified: false,
        moved: false,
        widget_edited: false,
        year_changed: false,
    };
    return {
        ...computed_variables,
        ...unused_variables,
    };
}

function post_process_message_preview($row: JQuery): void {
    const $content = $row.find(".message_content");
    update_elements($content);
    const id = rows.id($row);
    process_submessages({
        $row,
        message_id: id,
    });
    const $widget_content = $content.find(".widget-content");
    $widget_content.find("button, input, select").prop("disabled", true);
    $widget_content.find(".poll-edit-question, .poll-option-bar").hide();
}

export function show_sign_message_receipt_modal(message: Message): void {
    let selected_signing_user_id: number | undefined;
    let selected_representative_user_id: number | undefined;

    let invite_only;
    let is_web_public;
    let is_archived;
    if (message.is_stream) {
        const stream_id = message.stream_id;
        invite_only = stream_data.is_invite_only_by_stream_id(stream_id);
        is_web_public = stream_data.is_web_public(stream_id);
        is_archived = stream_data.is_stream_archived_by_id(stream_id);
    }

    const message_preview_body_args = get_message_container_for_preview(message);
    const modal_content_html = render_sign_message_receipt_modal({
        recipient_row_data: get_message_group_for_message_preview(message),
        message_container_data: {
            ...message_preview_body_args,
            message_list_id: "",
        },
        invite_only,
        is_web_public,
        is_archived,
    });

    function submit_message_receipt_signature(action: "sign" | "void"): void {
        const signing_account_select = document.querySelector(
            "#sign-message-signing-account-select",
        ) as HTMLSelectElement | null;
        if (signing_account_select !== null) {
            selected_signing_user_id = Number.parseInt(signing_account_select.value, 10);
        }
        const representative_account_select = document.querySelector(
            "#sign-message-representative-account-select",
        ) as HTMLSelectElement | null;
        if (representative_account_select !== null && representative_account_select.value !== "") {
            selected_representative_user_id = Number.parseInt(representative_account_select.value, 10);
        } else {
            selected_representative_user_id = undefined;
        }

        const url = "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature";
        const data: {
            action: "sign" | "void";
            signing_user_id?: number;
            representative_user_id?: number;
        } = {action};
        if (selected_signing_user_id !== undefined) {
            data.signing_user_id = selected_signing_user_id;
        }
        if (selected_representative_user_id !== undefined) {
            data.representative_user_id = selected_representative_user_id;
        }
        void channel.post({
            url,
            data,
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function submit_message_receipt_visibility(visibility: "private" | "public"): void {
        const url =
            "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature/visibility";
        void channel.post({
            url,
            data: {visibility},
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function add_visibility_button(
        $modal: JQuery,
        can_toggle_signature_page_visibility: boolean,
        signature_page_is_public: boolean,
    ): void {
        if (!can_toggle_signature_page_visibility || current_user.user_id !== message.sender_id) {
            return;
        }

        const visibility = signature_page_is_public ? "private" : "public";
        const button_label = signature_page_is_public
            ? $t({defaultMessage: "Make private"})
            : $t({defaultMessage: "Make public"});
        const $visibility_button = $(
            `<button type="button" class="modal__button dialog_exit_button sign-message-receipt-visibility-button">
                ${button_label}
            </button>`,
        );

        $modal.find(".modal__footer .dialog_exit_button").before($visibility_button);
        $visibility_button.on("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            dialog_widget.show_dialog_spinner();
            submit_message_receipt_visibility(visibility);
        });
    }

    function render_signing_account_selectors($modal: JQuery, accounts: SigningAccountOption[]): void {
        const signable_accounts = accounts.filter((account) => account.can_sign);
        if (signable_accounts.length === 0) {
            return;
        }

        const individual_accounts = accounts.filter((account) => account.is_individual_signer);
        const default_signing_account =
            signable_accounts.find((account) => account.is_current) ?? signable_accounts[0];
        if (default_signing_account === undefined) {
            return;
        }
        selected_signing_user_id = default_signing_account.user_id;

        const signing_options_html = signable_accounts
            .map((account) => {
                const selected_attr = account.user_id === default_signing_account.user_id ? "selected" : "";
                const escaped_name = $("<div>").text(account.display_name).html();
                const requires_representative = account.requires_representative ? "true" : "false";
                return `<option value="${account.user_id}" data-requires-representative="${requires_representative}" ${selected_attr}>${escaped_name}</option>`;
            })
            .join("");

        const representative_options_html = individual_accounts
            .map((account) => {
                const selected_attr = account.user_id === current_user.user_id ? "selected" : "";
                const escaped_name = $("<div>").text(account.display_name).html();
                return `<option value="${account.user_id}" ${selected_attr}>${escaped_name}</option>`;
            })
            .join("");

        const selectors_html = `
            <div class="sign-message-receipt-account-selectors">
                <label class="sign-message-receipt-account-label" for="sign-message-signing-account-select">
                    ${$t({defaultMessage: "Sign as"})}
                </label>
                <select id="sign-message-signing-account-select" class="sign-message-receipt-account-select">
                    ${signing_options_html}
                </select>
                <div id="sign-message-representative-account-container" class="sign-message-receipt-representative-container">
                    <label class="sign-message-receipt-account-label" for="sign-message-representative-account-select">
                        ${$t({defaultMessage: "Representative"})}
                    </label>
                    <select id="sign-message-representative-account-select" class="sign-message-receipt-account-select">
                        ${representative_options_html}
                    </select>
                </div>
            </div>
        `;
        const $selectors = $(selectors_html);
        $modal.find(".modal__footer").append($selectors);

        const update_representative_visibility = (): void => {
            const $selected_option = $selectors.find("#sign-message-signing-account-select option:selected");
            const requires_representative =
                $selected_option.attr("data-requires-representative") === "true";
            const $representative_container = $selectors.find(
                "#sign-message-representative-account-container",
            );
            if (requires_representative) {
                $representative_container.show();
                const selected_value = $selectors
                    .find("#sign-message-representative-account-select")
                    .val() as string | undefined;
                if (selected_value !== undefined && selected_value !== "") {
                    selected_representative_user_id = Number.parseInt(selected_value, 10);
                }
            } else {
                $representative_container.hide();
                selected_representative_user_id = undefined;
            }
        };

        $selectors.find("#sign-message-signing-account-select").on("change", function (this: HTMLElement) {
            const value = ($(this).val() as string | undefined) ?? "";
            selected_signing_user_id = Number.parseInt(value, 10);
            update_representative_visibility();
        });
        $selectors
            .find("#sign-message-representative-account-select")
            .on("change", function (this: HTMLElement) {
                const value = ($(this).val() as string | undefined) ?? "";
                selected_representative_user_id = Number.parseInt(value, 10);
            });

        update_representative_visibility();
    }

    function post_render(): void {
        const $message_preview_container = $("#sign-message-receipt-preview-container");
        register_message_preview_click_handlers($message_preview_container, message.sender_id);
        post_process_message_preview($message_preview_container.find(".message_row"));

        const $modal = $("#sign_message_receipt_modal");
        $modal.find(".modal__footer").addClass("sign-message-receipt-footer");
        const $submit_button = $modal.find(".dialog_submit_button");
        const $submit_button_text = $submit_button.find(".submit-button-text");
        const $submit_button_container = $modal.find(".modal__footer .dialog_submit_button_container");
        $modal.find(".dialog_submit_button").addClass("sign-message-receipt-sign-button");

        const visibility_url =
            "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature/visibility";
        const signing_accounts_url =
            "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature";

        void channel.get({
            url: signing_accounts_url,
            cache: false,
            success(data) {
                render_signing_account_selectors($modal, (data as SigningAccountsResponse).accounts);
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
            },
        });

        void channel.get({
            url: visibility_url,
            cache: false,
            success(data) {
                const visibility_data = data as SignatureVisibilityResponse;
                add_visibility_button(
                    $modal,
                    visibility_data.can_toggle_signature_page_visibility,
                    visibility_data.signature_page_is_public,
                );

                if (!visibility_data.show_signing_buttons) {
                    $submit_button_container.hide();
                    $modal.find(".sign-message-receipt-account-selectors").hide();
                    return;
                }

                const void_button_label = visibility_data.show_sign_edited_post_button
                    ? $t({defaultMessage: "VOID EDITED POST"})
                    : $t({defaultMessage: "VOID"});
                const submit_button_label = visibility_data.show_sign_edited_post_button
                    ? $t({defaultMessage: "SIGN EDITED POST"})
                    : $t({defaultMessage: "SIGN"});

                const $void_button = $(
                    `<button type="button" class="modal__button sign-message-receipt-void-button">${void_button_label}</button>`,
                );
                $submit_button_text.text(submit_button_label);
                $submit_button_container.before($void_button);
                $void_button.on("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dialog_widget.show_dialog_spinner();
                    submit_message_receipt_signature("void");
                });
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
            },
        });
    }

    dialog_widget.launch({
        html_heading: $t_html({
            defaultMessage: "Sign message receipt",
        }),
        html_body: modal_content_html,
        html_submit_button: $t_html({defaultMessage: "SIGN"}),
        html_exit_button: $t_html({defaultMessage: "Cancel"}),
        id: "sign_message_receipt_modal",
        form_id: "sign_message_receipt_form",
        on_click: () => {
            submit_message_receipt_signature("sign");
        },
        post_render,
        loading_spinner: true,
    });
}
